import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, FileText, TrendingUp, Upload } from 'lucide-react';
import { getAPIClient } from '../../api/client';

interface DocumentGap {
  document_type: string;
  missing_document: string;
  confidence: number;
  affected_questions: string[];
  failure_count: number;
  example_keywords: string[];
  recommendation: string;
  priority: 'high' | 'medium' | 'low';
}

interface DocumentGapResponse {
  project_id: string;
  analysis_period_days: number;
  total_queries: number;
  failed_queries: number;
  low_confidence_queries: number;  // NEW: Queries with answers but low confidence
  failure_rate: number;
  low_confidence_rate: number;  // NEW: Rate of low confidence queries
  identified_gaps: DocumentGap[];
  existing_documents: Array<{
    filename: string;
    type: string;
    source_type: string;
    size_kb: number;
    uploaded: string;
    chunks: number;
  }>;
  total_documents: number;  // NEW: Total count of all documents
  total_documents_with_chunks: number;  // NEW: Count of documents that have been chunked
  page: number;  // NEW: Current page number
  page_size: number;  // NEW: Page size
  total_pages: number;  // NEW: Total pages available
  recommendations_summary: string;
}

interface DocumentGapsPanelProps {
  projectId: string;
  onUploadClick?: (docType: string) => void;
}

export const DocumentGapsPanel: React.FC<DocumentGapsPanelProps> = ({
  projectId,
  onUploadClick
}) => {
  const [analysis, setAnalysis] = useState<DocumentGapResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    loadAnalysis();
  }, [projectId, currentPage, pageSize]);

  const loadAnalysis = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const client = getAPIClient();
      // Analytics queries can be slow - use extended timeout
      const data = await client.post<DocumentGapResponse>(
        '/analytics/document-gaps',
        {
          project_id: projectId,
          days_back: 30,
          min_failures: 3,
          confidence_threshold: 0.6,  // Include queries with confidence < 0.6 as low confidence
          page: currentPage,
          page_size: pageSize
        },
        {
          timeout: 300000  // 5 minute timeout for analytics
        }
      );
      
      setAnalysis(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analysis');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400" />
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Analyzing query patterns...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 dark:bg-red-900/20 rounded-lg">
        <p className="text-red-600 dark:text-red-400">Error: {error}</p>
        <button
          onClick={loadAnalysis}
          className="mt-2 text-sm text-red-700 dark:text-red-300 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!analysis) return null;

  const priorityColors = {
    high: 'border-red-500 dark:border-red-600 bg-red-50 dark:bg-red-900/20',
    medium: 'border-yellow-500 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20',
    low: 'border-gray-400 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
  };

  const priorityIcons = {
    high: <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />,
    medium: <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />,
    low: <AlertCircle className="h-5 w-5 text-gray-600 dark:text-gray-400" />
  };

  return (
    <div className="space-y-6">
      {/* Summary Card */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Document Coverage Analysis</h3>
          <button
            onClick={loadAnalysis}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
          >
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 dark:bg-slate-700 rounded-lg">
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{analysis.total_queries}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Queries ({analysis.analysis_period_days}d)</div>
          </div>
          
          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {analysis.total_queries - analysis.failed_queries - analysis.low_confidence_queries}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              High Confidence ({((1 - analysis.failure_rate - analysis.low_confidence_rate) * 100).toFixed(0)}%)
            </div>
          </div>
          
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{analysis.low_confidence_queries}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Low Confidence ({(analysis.low_confidence_rate * 100).toFixed(0)}%)
            </div>
          </div>
          
          <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">{analysis.failed_queries}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              No Answer ({(analysis.failure_rate * 100).toFixed(0)}%)
            </div>
          </div>
        </div>

        {analysis.recommendations_summary && (
          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
            <p className="text-sm text-blue-900 dark:text-blue-300">{analysis.recommendations_summary}</p>
          </div>
        )}
      </div>

      {/* Missing Documents */}
      {analysis.identified_gaps.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Missing Documents</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Upload these documents to improve answer coverage and confidence scores
            </p>
          </div>

          <div className="divide-y divide-gray-200">
            {analysis.identified_gaps.map((gap, idx) => (
              <div
                key={idx}
                className={`p-6 border-l-4 ${priorityColors[gap.priority]}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    {priorityIcons[gap.priority]}
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100">{gap.missing_document}</h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          gap.priority === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                          gap.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                          'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                        }`}>
                          {gap.priority.toUpperCase()} PRIORITY
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{gap.recommendation}</p>
                      
                      <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Queries affected:</span>
                          <span className="ml-2 font-semibold text-gray-900 dark:text-gray-100">{gap.failure_count}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Match confidence:</span>
                          <span className="ml-2 font-semibold text-gray-900 dark:text-gray-100">
                            {(gap.confidence * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>

                      {gap.example_keywords.length > 0 && (
                        <div className="mt-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Related keywords:</div>
                          <div className="flex flex-wrap gap-1">
                            {gap.example_keywords.map((kw, i) => (
                              <span
                                key={i}
                                className="px-2 py-1 text-xs bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded"
                              >
                                {kw}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {gap.affected_questions.length > 0 && (
                        <details className="mt-3">
                          <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                            Show example questions ({gap.affected_questions.length})
                          </summary>
                          <ul className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-400">
                            {gap.affected_questions.slice(0, 5).map((q, i) => (
                              <li key={i} className="pl-4 border-l-2 border-gray-300 dark:border-slate-600">
                                {q}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  </div>

                  {onUploadClick && (
                    <button
                      onClick={() => onUploadClick(gap.document_type)}
                      className="ml-4 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center space-x-2"
                    >
                      <Upload className="h-4 w-4" />
                      <span>Upload</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Existing Documents */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Existing Documents</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {analysis.total_documents.toLocaleString()} total documents ({analysis.total_documents_with_chunks.toLocaleString()} chunked) • 
                Showing page {analysis.page} of {analysis.total_pages}
              </p>
            </div>
            <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-slate-700 max-h-96 overflow-y-auto">
          {analysis.existing_documents.map((doc, idx) => (
            <div key={idx} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700">
              <div className="flex items-center space-x-3">
                <FileText className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">{doc.filename}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {doc.source_type} • {doc.size_kb.toFixed(0)} KB • {doc.chunks} chunks • 
                    Uploaded {new Date(doc.uploaded).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination Controls */}
        {analysis.total_pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, analysis.total_documents)} of {analysis.total_documents.toLocaleString()} documents
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Page {currentPage} of {analysis.total_pages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(analysis.total_pages, p + 1))}
                disabled={currentPage === analysis.total_pages}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* No Gaps - Success State */}
      {analysis.identified_gaps.length === 0 && (
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 text-center">
          <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-green-900 dark:text-green-300 mb-2">
            Excellent Document Coverage!
          </h3>
          <p className="text-sm text-green-700 dark:text-green-400">
            {analysis.low_confidence_queries > 0 || analysis.failed_queries > 0
              ? `Found ${analysis.failed_queries} failures and ${analysis.low_confidence_queries} low confidence queries, but no clear document pattern matches. Consider reviewing query patterns.`
              : 'No significant document gaps detected. The system can answer most questions with high confidence using the current document set.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default DocumentGapsPanel;

